import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, nextTick, computed } from 'vue';
import { useI18n } from '@/composables/use-i18n';
import { useCompareUrlState } from '@/composables/use-compare-url-state';
import { Lazy as PtLazy, Icon as PtIcon } from 'patsnap-biz';
import _ from 'lodash';
import AddPatent from '@/components/add-patent/AddPatent.vue';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { GetCompareList, AddComparePatent, type ApiGetCompareListResponse } from '@/infrastructure/api-functions/core-search-api/core-search-api';
import { useRoute, useRouter } from 'vue-router/composables';
import { useCompareInit } from '@/composables/use-compare-init';
import { create } from '@/utils/deferred';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';

interface PatentItem {
    PATENT_ID: string;
    PN: string;
    TITLE: string;
    page?: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ComparePatentList',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { compareId, page, rows, sort, _type, isTargetEmpty, rightId } = useCompareUrlState();
const { deferred } = useCompareInit();

const displayedSubTitle = computed(() => {
    const supportDisplayedTypes = ['family', 'cite', 'cited', 'mlt', 'mlt_image', 'semantic', 'condensed_family'];
    if (_type.value && supportDisplayedTypes.includes(_type.value)) {
        return t(`patent-view.list-type.${_type.value}`);
    }
    return t('patent-view.compareView.selectedPatents');
});

const initLoading = ref(false);
const patentList = ref<PatentItem[]>([]);
const totalNumber = ref(0);
const startIndex = ref(0);

const prePage = ref(page.value);
const nextPage = ref(page.value);

const hasNextPage = ref(false);
const hasPrevPage = ref(false);

const getCompareList = (page:number, callback:(res:ApiGetCompareListResponse) => void) => {
    const service = injectService<GetCompareList>(GetCompareList);
    service.getCompareList({
        compareId: compareId.value,
        page,
        rows: rows.value,
        sort: sort.value,
    }).then((res:ApiGetCompareListResponse) => {
        const _res = {
            ...res,
            LIST: res?.LIST?.map((item) => {
                return {
                    ...item,
                    page,
                };
            }),
        };
        callback && callback(_res);
    }).catch(err => {
        console.log(err);
    });
};

const initLoadList = () => {
    initLoading.value = true;
    getCompareList(page.value, (res:ApiGetCompareListResponse) => {
        if (isTargetEmpty.value) {
            const defaultId = _.get(res, 'LIST[0].PATENT_ID');
            if (defaultId) {
                router.push({
                    query: route.query,
                    params: {
                        ...route.params,
                        rightId: defaultId,
                    },
                });
            }
        }
        totalNumber.value = res.totalNumber || 0;
        patentList.value = res.LIST;

        hasPrevPage.value = page.value > 1;
        hasNextPage.value = page.value < res.totalPages;

        startIndex.value = (page.value - 1) * rows.value;
        initLoading.value = false;
    });
};

const nextPageLoading = ref(false);
const loadNextPage = () => {
    nextPageLoading.value = true;
    nextPage.value += 1;
    getCompareList(nextPage.value, (res:ApiGetCompareListResponse) => {
        patentList.value = Object.preventExtensions(_.concat(patentList.value, res.LIST));
        hasNextPage.value = nextPage.value < res.totalPages;
        nextPageLoading.value = false;
    });
};

const prevPageLoading = ref(false);
const loadPrevPage = () => {
    prevPageLoading.value = true;
    prePage.value -= 1;
    getCompareList(prePage.value, (res:ApiGetCompareListResponse) => {
        patentList.value = Object.preventExtensions(_.concat(res.LIST, patentList.value));
        hasPrevPage.value = prePage.value > 1;
        startIndex.value = (prePage.value - 1) * rows.value;
        prevPageLoading.value = false;
    });
};

const list = ref<HTMLElement>();
const addPatentToList = async (patent) => {
    if (!deferred.value || deferred.value?.completed) deferred.value = create();
    const patentId = patent.PATENT_ID;
    router.push({
        query: route.query,
        params: {
            ...route.params,
            rightId: patentId,
        },
    });
    patentList.value.unshift(patent);
    totalNumber.value += 1;
    await nextTick();
    const service = injectService<AddComparePatent>(AddComparePatent);
    service.addComparePatent({
        compare_id: compareId.value,
        patent_id: patentId,
    });
    if (list.value) {
        list.value.scrollTop = 0;
    }
};

const changePatent = (patent:PatentItem) => {
    if (!deferred.value || deferred.value?.completed) deferred.value = create();
    router.push({
        query: {
            ...route.query,
            page: patent.page,
        },
        params: {
            ...route.params,
            rightId: patent.PATENT_ID,
        },
    });
};

onMounted(() => {
    initLoadList();
});


return { __sfc: true,t, route, router, compareId, page, rows, sort, _type, isTargetEmpty, rightId, deferred, displayedSubTitle, initLoading, patentList, totalNumber, startIndex, prePage, nextPage, hasNextPage, hasPrevPage, getCompareList, initLoadList, nextPageLoading, loadNextPage, prevPageLoading, loadPrevPage, list, addPatentToList, changePatent, PtLazy, PtIcon, AddPatent }
}

})